import { useMemo, useState } from "react";
import { createFileRoute, Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, ClipboardCheck, Database, GitBranch, Inbox, Trash2 } from "lucide-react";
import { useGetDataAsset, useDataAssetLifecycle, getGetDataAssetQueryKey } from "@tubo/api-models/hooks/data-asset-api.hooks";
import { useRollbackDataAsset, getListAssetVersionsQueryKey } from "@tubo/api-models/hooks/asset-version-api.hooks";
import { useListAssetBatches } from "@tubo/api-models/hooks/asset-ingest-api.hooks";
import { useListAssetVersions } from "@tubo/api-models/hooks/asset-version-api.hooks";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@tubo/ui/components/ui/table";
import { DataAssetStatusEnum } from "@tubo/api-models/business/data-asset/data-asset.enums";
import { IngestionBatchStatusEnum } from "@tubo/api-models/business/data-asset/data-asset.enums";
import { ResourceTypeEnum } from "@tubo/api-models/business/platform/platform.enums";
import { LineageEntryButton } from "@client/components/semantic-platform/lineage/lineage-entry-button";
import { STATUS_LABEL } from "./index";
import { useAssetEvents } from "@client/components/semantic-platform/data-assets/asset-ws";
import { ChangeSetPanel } from "@client/components/semantic-platform/data-assets/change-sets/change-set-panel";
import { ViewPanel } from "@client/components/semantic-platform/data-assets/views/view-panel";
import { EntityPanel } from "@client/components/semantic-platform/data-assets/entities/entity-panel";
import { RelationInstancesPanel } from "@client/components/semantic-platform/data-assets/relations/relation-instances-panel";
import { WorkingRelationsPanel } from "@client/components/semantic-platform/data-assets/relations/working-relations-panel";
import { LineageGraphPanel } from "@client/components/semantic-platform/data-assets/lineage/lineage-graph-panel";
import { GovernancePanel } from "@client/components/semantic-platform/data-assets/governance/governance-panel";
import { CorpusPanel } from "@client/components/semantic-platform/data-assets/corpus/corpus-panel";
import { ConsumptionPanel } from "@client/components/semantic-platform/data-assets/consumption/consumption-panel";
import { VersionDiffDialog } from "@client/components/semantic-platform/data-assets/versions/version-diff-dialog";
import { AssetVersionDeleteDialog } from "@client/components/semantic-platform/data-assets/versions/asset-version-delete-dialog";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tubo/ui/components/ui/alert-dialog";

export const Route = createFileRoute("/_app/semantic-platform/data-assets/$assetId")({
  // ?tab= 深链接(26 号 P0:Tab 状态入 URL,刷新/分享不丢)
  validateSearch: (search: Record<string, unknown>): { ns?: string; tab?: Tab } => ({
    ...(typeof search.ns === "string" && search.ns ? { ns: search.ns } : {}),
    ...(isAssetTab(search.tab) ? { tab: search.tab } : {}),
  }),
  component: DataAssetDetailPage,
});

const BATCH_STATUS_LABEL: Record<string, string> = {
  [IngestionBatchStatusEnum.Receiving]: "接收中",
  [IngestionBatchStatusEnum.PreAnalyzed]: "已预分析",
  [IngestionBatchStatusEnum.Reviewing]: "审核中",
  [IngestionBatchStatusEnum.Applied]: "已应用",
  [IngestionBatchStatusEnum.Returned]: "已退回",
  [IngestionBatchStatusEnum.Failed]: "失败",
  [IngestionBatchStatusEnum.Cancelled]: "已取消",
};

const ASSET_TABS = ["overview", "corpus", "batches", "versions", "changeSets", "views", "entities", "relations", "lineage", "governance", "consumption"] as const;
type Tab = (typeof ASSET_TABS)[number];
const isAssetTab = (v: unknown): v is Tab => typeof v === "string" && ASSET_TABS.some((t) => t === v);

const FORMATION_STEPS = [
  { title: "绑定模型版本", description: "确定实体、属性和关系结构", icon: GitBranch },
  { title: "绑定语料并抽取", description: "把文献加入语料集，发起抽取任务", icon: Inbox },
  { title: "接收并审核", description: "处理批次，确认实体与事实", icon: ClipboardCheck },
  { title: "形成发布版本", description: "用变更集提交不可变版本", icon: CheckCircle2 },
  { title: "面向业务使用", description: "通过实体、视图和知识服务消费", icon: Database },
] as const;

function AssetFormationProgress({
  status,
  currentVersionId,
  onNext,
}: {
  status: string;
  currentVersionId?: string;
  onNext: (next: "corpus" | "batches" | "changeSets" | "entities") => void;
}) {
  const hasPublishedVersion = Boolean(currentVersionId)
    || status === DataAssetStatusEnum.Published
    || status === DataAssetStatusEnum.Suspended;
  const isArchived = status === DataAssetStatusEnum.Archived;
  const currentStep = hasPublishedVersion
    ? FORMATION_STEPS.length
    : status === DataAssetStatusEnum.Building || status === DataAssetStatusEnum.Publishable
      ? 3
      : 1;
  const nextAction = isArchived
    ? null
    : hasPublishedVersion
      ? { label: "查看实体数据", target: "entities" as const }
      : currentStep === 3
        ? { label: "打开变更集", target: "changeSets" as const }
        : { label: "去绑定语料并发起抽取", target: "corpus" as const };
  const statusCopy = isArchived
    ? "资产已归档，不能继续形成新版本。"
    : hasPublishedVersion
      ? "当前版本已形成，可进入实体和视图开始使用。"
      : currentStep === 3
        ? "工作区已有数据，下一步整理变更集并提交发布。"
        : "模型版本已绑定，下一步绑定语料并发起抽取生成候选。";

  return (
    <section className="mb-3 rounded-lg border bg-muted/10 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">形成进度</h2>
          <p className="mt-1 text-xs text-muted-foreground">{statusCopy}</p>
        </div>
        {nextAction && (
          <Button size="sm" onClick={() => onNext(nextAction.target)}>
            {nextAction.label} <ArrowRight className="size-3.5" />
          </Button>
        )}
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-5">
        {FORMATION_STEPS.map((step, index) => {
          const stepNumber = index + 1;
          const complete = currentStep === FORMATION_STEPS.length || stepNumber < currentStep;
          const current = !isArchived && currentStep < FORMATION_STEPS.length && stepNumber === currentStep;
          const Icon = step.icon;
          return (
            <div key={step.title} className="relative flex gap-2.5 md:pr-3">
              <div
                className={`flex size-7 shrink-0 items-center justify-center rounded-full border ${complete ? "border-primary bg-primary text-primary-foreground" : current ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-muted-foreground"}`}
              >
                {complete ? <Check className="size-3.5" /> : <Icon className="size-3.5" />}
              </div>
              <div className="min-w-0">
                <p className={`text-xs font-medium ${current ? "text-primary" : ""}`}>{step.title}</p>
                <p className="mt-0.5 text-[11px] leading-4 text-muted-foreground">{step.description}</p>
                {current && <p className="mt-1 text-[10px] font-medium text-primary">下一步</p>}
                {complete && <p className="mt-1 text-[10px] text-muted-foreground">已完成</p>}
              </div>
              {index < FORMATION_STEPS.length - 1 && (
                <ArrowRight className="absolute right-0 top-1.5 hidden size-3.5 text-muted-foreground/50 md:block" />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DataAssetDetailPage() {
  const { assetId } = Route.useParams(); const navigate = useNavigate();
  const { searchStr } = useLocation();
  const listSearch = new URLSearchParams(searchStr).get("ns");
  const queryClient = useQueryClient();
  const { tab: searchTab } = Route.useSearch();
  const [tab, setTabState] = useState<Tab>(searchTab ?? "overview");
  const setTab = (t: Tab): void => {
    setTabState(t);
    void navigate({ to: "/semantic-platform/data-assets/$assetId", params: { assetId }, search: (prev: Record<string, unknown>) => ({ ...prev, tab: t }), replace: true });
  };
  const [diffTarget, setDiffTarget] = useState<number | null>(null);

  const { data: asset, isLoading } = useGetDataAsset(assetId, { query: {} });
  const { data: batches } = useListAssetBatches(assetId, undefined, { query: { enabled: tab === "batches", refetchInterval: 15_000 } });
  const { data: versions } = useListAssetVersions(assetId, { query: { enabled: tab === "versions" || tab === "overview" } });

  // WS 批次/物化状态推送(25 §五 3 收口);断线由上述 REST refetchInterval 兜底
  useAssetEvents(assetId);

  const { mutate: lifecycleMutate } = useDataAssetLifecycle({
    mutation: {
      onSuccess: async (_data, vars) => {
        toast.success("生命周期操作成功");
        await queryClient.invalidateQueries({ queryKey: getGetDataAssetQueryKey(assetId) });
        void vars;
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : "操作失败"),
    },
  });

  const [deleteVersionTarget, setDeleteVersionTarget] = useState<{ id: string; label: string } | null>(null);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const currentVersion = versions?.find((v) => v.id === asset?.currentVersionId);

  // 头部注入域级 SecondaryNav(标题区:返回/名称/状态;操作区:停用/恢复/回滚),页面体不再自写标题栏
  const headerTitle = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" aria-label="返回列表" onClick={() => navigate({ to: "/semantic-platform/data-assets", search: listSearch ? { ns: listSearch } : {} })}>
          <ArrowLeft className="size-4" />
        </Button>
        {asset ? (
          <>
            <span className="text-sm font-semibold">{asset.name}</span>
            <Badge variant={asset.status === DataAssetStatusEnum.Published ? "default" : "secondary"}>
              {STATUS_LABEL[asset.status] ?? asset.status}
            </Badge>
          </>
        ) : (
          <span className="text-sm font-semibold">数据资产</span>
        )}
      </div>
    ),
    [asset, listSearch, navigate],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () =>
      asset ? (
        <div className="flex items-center gap-2">
          {asset.status === DataAssetStatusEnum.Published && (
            <Button variant="outline" size="sm" onClick={() => lifecycleMutate({ id: assetId, data: { action: "suspend", reason: "页面停用" } })}>
              停用
            </Button>
          )}
          {asset.status === DataAssetStatusEnum.Suspended && (
            <Button variant="outline" size="sm" onClick={() => lifecycleMutate({ id: assetId, data: { action: "restore" } })}>
              恢复
            </Button>
          )}
          {currentVersion && asset.revision != null && (
            <Button variant="outline" size="sm" onClick={() => setRollbackConfirmOpen(true)}>
              回滚到 {currentVersion.versionLabel}
            </Button>
          )}
          <LineageEntryButton resourceType={ResourceTypeEnum.DataAsset} resourceId={assetId} />
        </div>
      ) : null,
    [asset, currentVersion, lifecycleMutate, assetId],
  );
  useSecondaryNavActions(headerActions);

  if (isLoading || !asset) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">加载中…</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <RollbackConfirmDialog
        open={rollbackConfirmOpen}
        onOpenChange={setRollbackConfirmOpen}
        assetId={assetId}
        target={currentVersion ?? null}
        expectedRevision={asset.revision ?? null}
      />

      <AssetVersionDeleteDialog
        assetId={assetId}
        target={deleteVersionTarget}
        onClose={() => setDeleteVersionTarget(null)}
      />

      <div className="flex gap-1 border-b px-4 pt-2">
        {([["overview", "概览"], ["corpus", "来源"], ["batches", "接收批次"], ["versions", "发布版本"], ["changeSets", "变更集"], ["views", "视图"], ["entities", "实体"], ["relations", "关系"], ["lineage", "谱系图"], ["governance", "治理"], ["consumption", "消费"]] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-active={tab === key}
            className="border-b-2 border-transparent px-3 py-1.5 text-sm data-[active=true]:border-primary data-[active=true]:font-medium"
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto px-4 py-3">
        {tab === "overview" && (
          <div className="grid gap-3">
            <AssetFormationProgress
              status={asset.status}
              currentVersionId={asset.currentVersionId}
              onNext={(next) => setTab(next)}
            />
            <div className="rounded-lg border bg-surface/50 p-4">
              <dl className="grid grid-cols-[6rem_1fr] gap-y-2.5 text-sm">
                <dt className="text-muted-foreground">编码</dt>
                <dd className="font-mono text-xs">{asset.code}</dd>
                <dt className="text-muted-foreground">模型</dt>
                <dd>{asset.modelId}({asset.modelVersion})</dd>
                <dt className="text-muted-foreground">当前版本</dt>
                <dd>{currentVersion ? `${currentVersion.versionLabel}(${currentVersion.manifestChecksum.slice(0, 12)}…)` : "未发布"}</dd>
                <dt className="text-muted-foreground">修订号</dt>
                <dd>{asset.revision}</dd>
                {asset.desc && (
                  <>
                    <dt className="text-muted-foreground">说明</dt>
                    <dd>{asset.desc}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>
        )}

        {tab === "corpus" && <CorpusPanel assetId={assetId} />}
        {tab === "batches" && (
          <div className="rounded-lg border bg-surface/50 p-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-left">批次</TableHead>
                <TableHead className="text-left">状态</TableHead>
                <TableHead className="text-left">计数(新/更/冲/重)</TableHead>
                <TableHead className="text-left">接收时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(batches?.items ?? []).length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">暂无接收批次</TableCell></TableRow>
              )}
              {(batches?.items ?? []).map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <Link className="text-primary hover:underline" to="/semantic-platform/data-assets/batches/$batchId" params={{ batchId: b.id }} search={listSearch ? { ns: listSearch } : {}}>
                      {b.id.slice(-8)}
                    </Link>
                  </TableCell>
                  <TableCell><Badge variant="secondary">{BATCH_STATUS_LABEL[b.status] ?? b.status}</Badge></TableCell>
                  <TableCell className="text-xs">{b.newCount ?? 0}/{b.updateCount ?? 0}/{b.conflictCount ?? 0}/{b.duplicateCount ?? 0}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(b.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}

        {tab === "versions" && (
          <div className="rounded-lg border bg-surface/50 p-4">
          <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">版本</TableHead>
                  <TableHead className="text-left">等级</TableHead>
                  <TableHead className="text-left">实体/关系/事实</TableHead>
                  <TableHead className="text-left">发布时间</TableHead>
                  <TableHead className="w-44 text-left">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(versions ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">暂无发布版本</TableCell></TableRow>
                )}
              {(versions ?? []).map((v, idx) => (
                <TableRow key={v.id} className={v.id === asset.currentVersionId ? "bg-muted/40" : ""}>
                  <TableCell className="font-medium">{v.versionLabel}{v.id === asset.currentVersionId && <Badge className="ml-2" variant="default">当前</Badge>}</TableCell>
                  <TableCell className="text-xs">{v.changeLevel}</TableCell>
                  <TableCell className="text-xs">{v.entityCount}/{v.relationCount}/{v.factCount}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(v.publishedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {idx + 1 < (versions ?? []).length && (
                        <Button variant="outline" size="sm" onClick={() => setDiffTarget(idx)}>
                          与前版差异
                        </Button>
                      )}
                      {v.id !== asset.currentVersionId && (
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setDeleteVersionTarget({ id: v.id, label: v.versionLabel })}
                        >
                          <Trash2 className="mr-1 size-3.5" /> 删除
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}
        {tab === "changeSets" && <ChangeSetPanel assetId={assetId} />}
        {tab === "views" && <ViewPanel assetId={assetId} currentVersionId={asset.currentVersionId} />}
        {tab === "entities" && <EntityPanel assetId={assetId} />}
        {tab === "relations" && (
          <div className="grid gap-3">
            <WorkingRelationsPanel assetId={assetId} assetRevision={asset.revision} />
            <div className="border-t pt-3">
              <p className="mb-2 text-xs text-muted-foreground">以下为已发布版本 {asset.currentVersionId ? asset.currentVersionId.slice(-8) : "（尚未发布）"} 的图谱投影快照（只读）：</p>
              <RelationInstancesPanel assetId={assetId} versionId={asset.currentVersionId} />
            </div>
          </div>
        )}

        {tab === "lineage" && <LineageGraphPanel assetId={assetId} versionId={asset.currentVersionId} />}
        {tab === "governance" && <GovernancePanel assetId={assetId} />}
        {tab === "consumption" && <ConsumptionPanel assetId={assetId} currentVersionId={asset.currentVersionId} />}
      </div>

      <VersionDiffDialog
        version={diffTarget != null ? (versions ?? [])[diffTarget] ?? null : null}
        baseVersionId={diffTarget != null ? (versions ?? [])[diffTarget + 1]?.id ?? null : null}
        open={diffTarget != null}
        onOpenChange={(v) => !v && setDiffTarget(null)}
      />
    </div>
  );
}

/** 回滚确认弹窗(自含 mutation 与缓存失效;页面体只管开关) */
function RollbackConfirmDialog({
  open,
  onOpenChange,
  assetId,
  target,
  expectedRevision,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetId: string;
  target: { id: string; versionLabel: string } | null;
  expectedRevision: number | null;
}) {
  const queryClient = useQueryClient();
  const { mutate: rollbackMutate } = useRollbackDataAsset({
    mutation: {
      onSuccess: async () => {
        toast.success("已回滚到目标版本");
        await queryClient.invalidateQueries({ queryKey: getGetDataAssetQueryKey(assetId) });
        await queryClient.invalidateQueries({ queryKey: getListAssetVersionsQueryKey(assetId) });
      },
      // biome-ignore lint: orval 泛型推导
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : "回滚失败"),
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>回滚到 {target?.versionLabel}</AlertDialogTitle>
          <AlertDialogDescription>
            回滚将把资产内容恢复到该发布版本,当前版本之后的数据变更将被覆盖,确认回滚?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() =>
              target &&
              expectedRevision != null &&
              rollbackMutate({ id: assetId, data: { targetVersionId: target.id, expectedRevision, reason: "页面回滚" } })
            }
          >
            确认回滚
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { BATCH_STATUS_LABEL };
